interface Laboratory {
  uid: string;
  institution: string;
  director: string;
  city: string;
  country: string;
}

interface LaboratoriesSearchState {
  query: string;
  results: Laboratory[];
  loading: boolean;
  totalResults: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  debounceTimer: number | null;
}

export default () => ({
  query: '',
  results: [] as Laboratory[],
  loading: false,
  totalResults: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 10,
  debounceTimer: null as number | null,

  init() {
    // Get initial values from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('s');
    const pageParam = urlParams.get('page');

    if (searchParam) {
      this.query = searchParam;
    }

    if (pageParam) {
      this.currentPage = parseInt(pageParam, 10) || 1;
    }

    // Fetch initial data
    this.fetchData();
  },

  onSearchInput(value: string) {
    this.query = value;

    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Set new timer for debounced search
    this.debounceTimer = window.setTimeout(() => {
      this.currentPage = 1; // Reset to first page on new search
      this.fetchData();
    }, 300);
  },

  async fetchData() {
    this.loading = true;

    // Build URL
    let url = '/actions/craft-meilisearch/search/filter?index=laboratories';

    // Add search query if provided
    if (this.query && this.query.trim() !== '') {
      url += `&settings[q]=${encodeURIComponent(this.query)}`;
    }

    // Add pagination
    url += `&settings[limit]=${this.pageSize}`;
    url += `&settings[offset]=${(this.currentPage - 1) * this.pageSize}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      this.results = data.hits || [];
      this.totalResults = data.estimatedTotalHits || 0;
      this.totalPages = Math.ceil(this.totalResults / this.pageSize);

      // Update URL with current search state
      this.updateUrl();
    } catch (error) {
      console.error('Error fetching laboratories:', error);
      this.results = [];
      this.totalResults = 0;
      this.totalPages = 0;
    } finally {
      this.loading = false;
    }
  },

  updateUrl() {
    const url = new URL(window.location.href);

    // Update search parameter
    if (this.query && this.query.trim() !== '') {
      url.searchParams.set('s', this.query);
    } else {
      url.searchParams.delete('s');
    }

    // Update page parameter
    if (this.currentPage > 1) {
      url.searchParams.set('page', this.currentPage.toString());
    } else {
      url.searchParams.delete('page');
    }

    window.history.pushState({}, '', url);
  },

  goToPage(pageNum: number) {
    if (pageNum < 1 || pageNum > this.totalPages) {
      return;
    }

    this.currentPage = pageNum;
    this.fetchData();

    // Scroll to top of the block smoothly
    const blockElement = document.getElementById('laboratories-search-block');
    if (blockElement) {
      blockElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  },

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  },

  previousPage() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  },

  get visiblePages(): number[] {
    const range = 2; // Show 2 pages on each side of current
    const pages: number[] = [];
    const start = Math.max(1, this.currentPage - range);
    const end = Math.min(this.totalPages, this.currentPage + range);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  },

  get hasResults(): boolean {
    return this.results.length > 0;
  },

  get hasNoResults(): boolean {
    return !this.loading && this.results.length === 0;
  }
});
